import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { superAdminApi } from '../services/super-admin-api'

export interface UserOrganizationMembership {
  id: string
  name: string
  role: 'admin' | 'professional' | 'assistant'
  is_active: boolean
  permissions: Record<string, boolean>
}

export interface User {
  profile_id: string
  email: string
  full_name: string | null
  phone: string | null
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  has_auth_account: boolean
  is_blocked: boolean
  blocked_at: string | null
  blocked_reason: string | null
  is_super_admin: boolean
  organizations: UserOrganizationMembership[]
}

interface UserStats {
  total_users: number
  active_users: number
  blocked_users: number
  super_admins: number
  members: number
  new_users_30d: number
}

interface ListUsersParams {
  page?: number
  page_size?: number
  search?: string
  organization_id?: string
  role?: 'admin' | 'professional' | 'assistant'
  is_blocked?: boolean
}

interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

interface CreateUserPayload {
  email: string
  full_name: string
  phone?: string | null
  role: 'admin' | 'professional' | 'assistant'
  organization_id?: string
  password?: string
}

interface UpdateUserPayload {
  email?: string
  full_name?: string
  phone?: string | null
}

interface UpsertUserOrganizationPayload {
  profileId: string
  organizationId: string
  role?: 'admin' | 'professional' | 'assistant'
  is_active?: boolean
  permissions?: Record<string, boolean>
}

interface ImpersonateResponse {
  data: {
    session_token: string
    expires_at: string
    action_url?: string
  }
}

interface AdminResetPasswordResponse {
  success: boolean
  message: string
  temporaryPassword: string
  email: string
  note: string
}

export function useUsers(params: ListUsersParams = {}) {
  return useQuery({
    queryKey: ['super-admin', 'users', params],
    queryFn: async (): Promise<PaginatedResponse<User>> => {
      return superAdminApi.get<PaginatedResponse<User>>(
        '/users',
        params as Record<string, string | number | boolean | undefined>
      )
    },
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['super-admin', 'users', id],
    queryFn: async (): Promise<User> => {
      const response = await superAdminApi.get<{ data: User }>(`/users/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function useUserStats(organizationId?: string) {
  return useQuery({
    queryKey: ['super-admin', 'users', 'stats', organizationId],
    queryFn: async (): Promise<UserStats> => {
      const response = await superAdminApi.get<{ data: UserStats }>('/users/stats', {
        organization_id: organizationId,
      })
      return response.data
    },
  })
}

export function useBlockUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<User> => {
      const response = await superAdminApi.post<{ data: User }>(`/users/${id}/block`, { reason })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', variables.id] })
    },
  })
}

export function useUnblockUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<User> => {
      const response = await superAdminApi.post<{ data: User }>(`/users/${id}/unblock`)
      return response.data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', id] })
    },
  })
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: async (profileId: string) => {
      const response = await superAdminApi.post<ImpersonateResponse>(`/users/${profileId}/impersonate`)
      return response.data
    },
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      return superAdminApi.post<{ data: User; temporaryPassword: string }>('/users', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', 'stats'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ profileId, data }: { profileId: string; data: UpdateUserPayload }) => {
      const response = await superAdminApi.patch<{ data: User }>(`/users/${profileId}`, data)
      return response.data
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', user.profile_id] })
    },
  })
}

export function useAddUserOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ profileId, organizationId, role }: UpsertUserOrganizationPayload) => {
      if (!role) {
        throw new Error('Role is required to add a user to an organization')
      }

      const response = await superAdminApi.post<{ data: User }>(`/users/${profileId}/organizations`, {
        organization_id: organizationId,
        role,
      })
      return response.data
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', user.profile_id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
    },
  })
}

export function useUpdateUserOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ profileId, organizationId, role, is_active, permissions }: UpsertUserOrganizationPayload) => {
      const response = await superAdminApi.patch<{ data: User }>(`/users/${profileId}/organizations/${organizationId}`, {
        role,
        is_active,
        permissions,
      })
      return response.data
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', user.profile_id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
    },
  })
}

export function useRemoveUserOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ profileId, organizationId }: { profileId: string; organizationId: string }) => {
      const response = await superAdminApi.delete<{ data: User }>(`/users/${profileId}/organizations/${organizationId}`)
      return response.data
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users', user.profile_id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'members'] })
    },
  })
}

interface OrganizationOption {
  id: string
  name: string
}

export function useOrganizationsSelect() {
  return useQuery({
    queryKey: ['super-admin', 'organizations-select'],
    queryFn: async (): Promise<OrganizationOption[]> => {
      const response = await superAdminApi.get<PaginatedResponse<OrganizationOption>>('/organizations', {
        page: 1,
        page_size: 500,
        sort_by: 'name',
        sort_order: 'asc',
      })

      return response.data.map((organization) => ({
        id: organization.id,
        name: organization.name,
      }))
    },
  })
}

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: async (profileId: string) => {
      const response = await superAdminApi.post<AdminResetPasswordResponse>(`/users/${profileId}/reset-password`, {
        send_email: false,
      })
      return response
    },
  })
}

export function useConfirmEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (profileId: string) => {
      await superAdminApi.post(`/users/${profileId}/confirm-email`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'users'] })
    },
  })
}
